import { RefObject, useLayoutEffect, useState } from 'react';

import { hasPlaybackSnapshot } from './useVideoPlaybackPreserve';

type UseVideoNoPosterRevealOptions = {
  hasPoster: boolean;
  hasVideoWithoutPoster: boolean;
  videoRef: RefObject<HTMLVideoElement | null>;
  videoUrl: string;
};

// Survives remounts (store switch + router.refresh) so the fade only runs once per URL.
const revealedVideoUrls = new Set<string>();

const markVideoUrlRevealed = (videoUrl: string) => {
  if (videoUrl) {
    revealedVideoUrls.add(videoUrl);
  }
};

const wasVideoUrlRevealed = (videoUrl: string) =>
  Boolean(videoUrl && revealedVideoUrls.has(videoUrl));

/**
 * Black placeholder until the first frame when no preview_image is set.
 * Re-renders after store change / refresh skip the fade if the URL was already shown.
 */
const useVideoNoPosterReveal = ({
  hasPoster,
  hasVideoWithoutPoster,
  videoRef,
  videoUrl,
}: UseVideoNoPosterRevealOptions) => {
  const skipRevealTransition =
    wasVideoUrlRevealed(videoUrl) || hasPlaybackSnapshot(videoUrl);

  const [isVideoReady, setIsVideoReady] = useState(
    () => !hasVideoWithoutPoster || skipRevealTransition,
  );

  const markVideoReady = () => {
    if (hasVideoWithoutPoster && videoUrl) {
      markVideoUrlRevealed(videoUrl);
    }

    setIsVideoReady(true);
  };

  const revealVideoIfBuffered = () => {
    if (!hasVideoWithoutPoster) {
      setIsVideoReady(true);
      return;
    }

    const video = videoRef.current;
    if (video && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      markVideoReady();
    }
  };

  useLayoutEffect(() => {
    revealVideoIfBuffered();
  }, [hasVideoWithoutPoster, videoUrl]);

  const handleVideoReady = () => {
    markVideoReady();
  };

  const handleVideoPlay = () => {
    markVideoReady();
  };

  return {
    handleVideoPlay,
    handleVideoReady,
    isVideoVisible: isVideoReady || hasPoster,
    revealVideoIfBuffered,
    skipRevealTransition: skipRevealTransition || wasVideoUrlRevealed(videoUrl),
  };
};

export { useVideoNoPosterReveal };
